// ==============================================
// Plan editor: every setting on a plan, laid out in the eight sections of
// planSections.ts, one section per tab, with an ⓘ on every setting, and a
// Save bar at the top that keeps count of unsaved changes across every tab.
// ==============================================

import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import type { PackageChange } from '../../../types/subscription';
import { PackageTokenSettings } from '../PackageTokenSettings';
import { BasemapPicker } from './BasemapPicker';
import { FeatureListEditor } from './FeatureListEditor';
import { InfoTip } from './InfoTip';
import { OFFLINE_CAPABLE_SOURCES, BASEMAP_OPTIONS, type PackageFormData } from './packageForm';
import { PricingCard } from '../../embed/PricingCard';
import type { Package } from '../../../types/subscription';
import { changedFields, describeChange } from './planChanges';
import { SPECIAL_SLUGS, isTidySlug, planChecks, suggestSlug, type PlanCheck } from './planChecks';
import { PLAN_SECTIONS, STORAGE_PRESETS_GB, fieldsOf, sectionKeys, type PlanField, type PlanSection } from './planSections';

const input = 'px-3 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:border-cyan-500 focus:outline-none';
const OFFLINE_INVALID = 'Offline downloads are enabled — select at least one offline basemap source.';

const sameValue = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
const nzd = (cents: number) => `NZ$${(cents / 100).toFixed(2)}`;

function Switch({ checked, onChange, label }: { checked: boolean; onChange: (on: boolean) => void; label: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 ${
        checked ? 'bg-cyan-500 border-cyan-500' : 'bg-slate-700 border-slate-600'
      }`}
    >
      <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
  );
}

/**
 * A dollars box that keeps cents. What is typed shows as typed; only a valid
 * amount reaches the form, and leaving the box puts back the last valid one,
 * so a cleared box never saves "not a number".
 */
function MoneyInput({ id, cents, onChange }: { id: string; cents: number; onChange: (cents: number) => void }) {
  const [text, setText] = useState(String(cents / 100));
  useEffect(() => {
    if (Math.round(parseFloat(text) * 100) !== cents) setText(String(cents / 100));
    // Only follow changes from outside the box, not every keystroke.
  }, [cents]);
  return (
    <input id={id} type="number" step="0.01" min="0" value={text}
      onChange={(e) => {
        setText(e.target.value);
        const dollars = parseFloat(e.target.value);
        if (Number.isFinite(dollars) && dollars >= 0) onChange(Math.round(dollars * 100));
      }}
      onBlur={() => setText(String(cents / 100))}
      className={`${input} w-36 text-right tabular-nums`} />
  );
}

/** Label, ⓘ and hint on the left; the control on the right (stacked on narrow screens). */
function Row({ field, htmlFor, hint, children, wide }: { field: PlanField; htmlFor?: string; hint?: ReactNode; children: ReactNode; wide?: boolean }) {
  const Label = htmlFor ? 'label' : 'span';
  return (
    <div className={`py-4 border-b border-slate-700/60 last:border-0 ${wide ? 'space-y-3' : 'flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'}`}>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <Label {...(htmlFor ? { htmlFor } : {})} className="text-[15px] font-medium text-slate-100">{field.label}</Label>
          <InfoTip label={field.label}>{field.info}</InfoTip>
          {field.badge && (
            <span className={`rounded px-2 py-0.5 text-[13px] font-semibold ${
              field.badgeTone === 'red' ? 'bg-red-500/15 text-red-300' : 'bg-amber-500/15 text-amber-300'
            }`}>{field.badge}</span>
          )}
        </div>
        {hint && <div className="mt-1 text-sm text-slate-400">{hint}</div>}
      </div>
      <div className={wide ? '' : 'shrink-0'}>{children}</div>
    </div>
  );
}

/** The plan as a Package, so the preview uses the real pricing card. */
const asPackage = (form: PackageFormData) => ({
  ...form,
  id: 'preview',
  description: form.description || null,
  stripe_product_id: null,
  stripe_price_id: null,
  created_at: '',
  updated_at: '',
}) as Package;

/** The pricing card exactly as customers see it, and anything about the plan that needs attention. */
function PlanSidebar({ form, checks }: { form: PackageFormData; checks: PlanCheck[] }) {
  return (
    <div className="grid gap-6 sm:grid-cols-2 sm:items-start xl:block xl:space-y-6">
      <section aria-label="Pricing card preview" className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-400">What customers see</h2>
        <div className={`pt-4 ${form.visibility === 'active' ? '' : 'opacity-50'}`}>
          <PricingCard pkg={asPackage(form)} currency="NZD" disableScale onSelect={() => {}} />
        </div>
        {form.visibility !== 'active' && (
          <p className="text-sm text-slate-400">Not on the pricing page while the plan is {form.visibility}.</p>
        )}
      </section>
      <section aria-labelledby="plan-checks-title" className="space-y-3">
        <h2 id="plan-checks-title" className="text-sm font-semibold uppercase tracking-wide text-slate-400">Plan checks</h2>
        {checks.length === 0 ? (
          <p className="text-[15px] text-slate-300">Nothing needs attention.</p>
        ) : (
          <ul className="space-y-2">
            {checks.map((c) => (
              <li key={c.message} className={`flex gap-3 rounded-lg border px-3 py-2 text-sm ${
                c.level === 'problem' ? 'border-red-500/40 bg-red-500/10 text-red-200' : 'border-amber-500/40 bg-amber-500/10 text-amber-200'
              }`}>
                <span className="font-semibold">{c.level === 'problem' ? 'Fix' : 'Check'}</span>
                <span>{c.message}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

const when = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'Date not recorded';

/** A plan's past changes, newest first: when, who, why, and each setting before and after. */
function PlanHistory({ history, hidden }: { history: PackageChange[]; hidden: boolean }) {
  return (
    <section id="plan-history" role="tabpanel" aria-labelledby="plan-history-title" hidden={hidden} className="rounded-xl border border-slate-700 bg-slate-800/50 p-6">
      <h2 id="plan-history-title" className="text-lg font-semibold text-white">History</h2>
      <p className="mt-1 text-sm text-slate-400">Every saved change to this plan, newest first.</p>
      {history.length === 0 ? (
        <p className="mt-4 text-[15px] text-slate-300">No changes recorded yet.</p>
      ) : (
        <ol className="mt-4 space-y-4">
          {history.map((h) => (
            <li key={h.id} className="rounded-lg border border-slate-700 bg-slate-900/40 p-4">
              <p className="text-[15px] text-white">
                <span className="font-medium">{when(h.changed_at)}</span>
                <span className="text-slate-400"> · {h.changed_by ? (h.changed_by.name || h.changed_by.email) : 'Admin not recorded'}</span>
              </p>
              {h.reason && <p className="mt-1 text-[15px] text-slate-200">“{h.reason}”</p>}
              {h.action === 'package.created' ? (
                <p className="mt-2 text-sm text-slate-300">Created the plan.</p>
              ) : (
                <ul className="mt-2 space-y-1 text-sm text-slate-300">
                  {Object.entries(h.new_values).map(([key, to]) => {
                    const c = describeChange(key, h.old_values?.[key], to);
                    return (
                      <li key={key}>
                        <span className="text-slate-200">{c.label}:</span> {c.from} → <span className="text-cyan-300">{c.to}</span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

export function PackageEditor({
  pkg,
  onSave,
  onCancel,
  isLoading,
  allFeatures,
  isNew = false,
  subscriberCount,
  history,
  title,
}: {
  pkg: PackageFormData;
  /** The reason is the admin's optional note for the plan's history. */
  onSave: (data: PackageFormData, reason?: string) => void;
  onCancel: () => void;
  isLoading: boolean;
  allFeatures: string[];
  /** A plan being created or cloned saves straight away; an existing plan is reviewed first. */
  isNew?: boolean;
  /** Active subscriptions on this plan, for the review; unknown while loading. */
  subscriberCount?: number | null;
  history?: PackageChange[];
  /** The page heading, e.g. "Edit Package", shown under the way back to the list. */
  title?: string;
}) {
  const [formData, setFormData] = useState<PackageFormData>(pkg);
  const [reviewing, setReviewing] = useState(false);
  const [slugUnlocked, setSlugUnlocked] = useState(isNew);
  const [reason, setReason] = useState('');
  const [tab, setTab] = useState<string>(PLAN_SECTIONS[0].id);
  const reviewRef = useRef<HTMLElement>(null);
  const panelsRef = useRef<HTMLDivElement>(null);
  const set = <K extends keyof PackageFormData>(key: K, v: PackageFormData[K]) => setFormData((f) => ({ ...f, [key]: v }));

  // A package with offline downloads enabled must allow at least one offline
  // source — a saved empty list means zero offline sources on-device.
  const offlineSourcesInvalid = formData.offline_packs && formData.allowed_basemaps_offline.length === 0;

  const changedSections = useMemo(
    () => PLAN_SECTIONS.filter((s) => sectionKeys(s).some((k) => !sameValue(formData[k], pkg[k]))),
    [formData, pkg],
  );
  const changeCount = useMemo(
    () => PLAN_SECTIONS.flatMap(fieldsOf).filter((f) => f.keys.some((k) => !sameValue(formData[k], pkg[k]))).length,
    [formData, pkg],
  );
  const dirty = changeCount > 0;
  const checks = useMemo(() => planChecks(formData), [formData]);

  /** A saved plan's URL key changes only after the admin is told what it breaks. */
  const unlockSlug = (next?: string) => {
    const why = SPECIAL_SLUGS[pkg.slug]
      ?? 'Other parts of the system find this plan by its URL key, and checkout links already shared with the old key will stop working.';
    if (!window.confirm(`${why} Change the URL key anyway?`)) return;
    setSlugUnlocked(true);
    if (next !== undefined) set('slug', next);
  };
  const changes = useMemo(() => (reviewing ? changedFields(pkg, formData) : []), [reviewing, pkg, formData]);

  useEffect(() => {
    if (!dirty) setReviewing(false);
  }, [dirty]);
  useEffect(() => {
    if (reviewing) reviewRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
  }, [reviewing]);

  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const cancel = () => {
    if (dirty && !window.confirm('Discard your unsaved changes to this plan?')) return;
    onCancel();
  };

  const tabIds = [...PLAN_SECTIONS.map((s) => s.id), ...(history ? ['history'] : [])];

  /** Show one section; it starts at its top, not wherever the last one was scrolled to. */
  const openTab = (id: string) => {
    setTab(id);
    panelsRef.current?.scrollTo?.({ top: 0 });
  };

  // Arrow keys move along the menu, as in any tab list.
  const onTabKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    const step = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 }[e.key];
    const at = tabIds.indexOf(tab);
    const next = e.key === 'Home' ? 0 : e.key === 'End' ? tabIds.length - 1 : step ? (at + step + tabIds.length) % tabIds.length : -1;
    if (next < 0) return;
    e.preventDefault();
    openTab(tabIds[next]);
    document.getElementById(`plan-tab-${tabIds[next]}`)?.focus();
  };

  const renderField = (field: PlanField) => {
    const id = `plan-field-${field.keys[0]}`;
    switch (field.kind) {
      case 'toggle': {
        const k = field.key;
        return (
          <Row key={k} field={field} hint={field.hint}>
            <Switch checked={formData[k]} onChange={(on) => set(k, on)} label={field.label} />
          </Row>
        );
      }
      case 'text':
      case 'textarea': {
        const k = field.key;
        return (
          <Row key={k} field={field} htmlFor={id} hint={field.hint} wide>
            {field.kind === 'text' ? (
              <input id={id} type="text" value={formData[k]} placeholder={field.placeholder}
                onChange={(e) => set(k, e.target.value)} className={`${input} w-full max-w-xl`} />
            ) : (
              <textarea id={id} rows={2} value={formData[k]} placeholder={field.placeholder}
                onChange={(e) => set(k, e.target.value)} className={`${input} w-full`} />
            )}
          </Row>
        );
      }
      case 'slug': {
        const k = field.key;
        const suggested = suggestSlug(formData.name);
        return (
          <Row key={k} field={field} htmlFor={slugUnlocked ? id : undefined} hint={field.hint} wide>
            {slugUnlocked ? (
              <input id={id} type="text" value={formData[k]} placeholder={field.placeholder}
                onChange={(e) => set(k, e.target.value)} className={`${input} w-full max-w-xl`} />
            ) : (
              <div className="flex flex-wrap items-center gap-3">
                <code className="rounded-md border border-slate-600 bg-slate-900/50 px-3 py-2 text-[15px] text-white">{formData[k]}</code>
                <span className="rounded px-2 py-0.5 text-[13px] font-semibold bg-slate-700 text-slate-300">Locked</span>
                <button type="button" onClick={() => unlockSlug()} aria-label="Change the URL key"
                  className="text-sm font-medium text-cyan-400 hover:text-cyan-300">Change…</button>
                {!isTidySlug(formData[k]) && !SPECIAL_SLUGS[formData[k]] && suggested && (
                  <button type="button" onClick={() => unlockSlug(suggested)}
                    className="text-sm font-medium text-cyan-400 hover:text-cyan-300">Use "{suggested}"</button>
                )}
              </div>
            )}
          </Row>
        );
      }
      case 'number': {
        const k = field.key;
        return (
          <Row key={k} field={field} htmlFor={id} hint={field.hint}>
            <span className="inline-flex items-center gap-2 text-slate-300">
              <input id={id} type="number" min={field.min} max={field.max} value={formData[k]}
                onChange={(e) => {
                  let n = parseInt(e.target.value);
                  if (Number.isNaN(n)) n = field.fallback ?? 0;
                  if (field.clamp) n = Math.max(field.min ?? n, Math.min(field.max ?? n, n));
                  set(k, n);
                }}
                className={`${input} w-36 text-right tabular-nums`} />
              {field.suffix && <span className="text-sm">{field.suffix}</span>}
            </span>
          </Row>
        );
      }
      case 'money': {
        const k = field.key;
        return (
          <Row key={k} field={field} htmlFor={id}
            hint={
              field.quarterly ? `Billed quarterly: ${nzd(formData[k] * 3)} (monthly price × 3)`
              : field.zeroWarning && formData[k] === 0 ? <span className="text-amber-300">{field.zeroWarning}</span>
              : field.hint
            }>
            <span className="inline-flex items-center gap-2 text-slate-300">
              <span className="text-sm">NZ$</span>
              <MoneyInput id={id} cents={formData[k]} onChange={(c) => set(k, c)} />
              {field.suffix && <span className="text-sm">{field.suffix}</span>}
            </span>
          </Row>
        );
      }
      case 'select': {
        const k = field.key;
        const numeric = typeof field.options[0].value === 'number';
        return (
          <Row key={k} field={field} htmlFor={id} hint={field.hint}>
            <select id={id} value={String(formData[k])}
              onChange={(e) => setFormData((f) => ({ ...f, [k]: numeric ? parseInt(e.target.value) : e.target.value }))}
              className={`${input} min-w-[12rem]`}>
              {field.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Row>
        );
      }
      case 'storage': {
        const k = field.key;
        const on = formData[field.library];
        const mb = formData[k];
        const preset = STORAGE_PRESETS_GB.some((gb) => gb * 1024 === mb);
        return (
          <Row key={k} field={field} hint={field.hint}>
            <div role="group" aria-label={field.label}
              className={`inline-flex flex-wrap items-center gap-1 rounded-lg border border-slate-600 bg-slate-900/50 p-1 ${on ? '' : 'opacity-50'}`}>
              {STORAGE_PRESETS_GB.map((gb) => (
                <button key={gb} type="button" disabled={!on} aria-pressed={mb === gb * 1024}
                  onClick={() => set(k, gb * 1024)}
                  className={`rounded-md px-3 py-1.5 text-sm font-medium disabled:cursor-not-allowed ${
                    mb === gb * 1024 ? 'bg-cyan-500/20 text-cyan-200 ring-1 ring-cyan-500' : 'text-slate-300 hover:bg-slate-700'
                  }`}>
                  {gb} GB
                </button>
              ))}
              {!preset && <span className="px-3 py-1.5 text-sm text-slate-200 ring-1 ring-cyan-500 rounded-md">{mb} MB (custom)</span>}
            </div>
          </Row>
        );
      }
      case 'count': {
        const k = field.key;
        const n = formData[k];
        const unlimited = n === 0;
        const step = 'h-9 w-9 rounded-lg border border-slate-600 text-lg text-white hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed';
        return (
          <Row key={k} field={field} hint={field.hint}>
            <span className="inline-flex items-center gap-3">
              <button type="button" aria-label={`Fewer ${field.unit}`} disabled={unlimited || n <= 1} onClick={() => set(k, n - 1)} className={step}>−</button>
              <output className="w-20 text-center text-[15px] font-semibold text-white tabular-nums">{unlimited ? 'Unlimited' : n}</output>
              <button type="button" aria-label={`More ${field.unit}`} disabled={unlimited} onClick={() => set(k, n + 1)} className={step}>+</button>
              <span className="ml-2 inline-flex items-center gap-2 text-sm text-slate-300">
                Unlimited
                <Switch checked={unlimited} onChange={(on) => set(k, on ? 0 : 1)} label={`Unlimited ${field.unit}`} />
              </span>
            </span>
          </Row>
        );
      }
      case 'slider': {
        const k = field.key;
        const said = field.describe(formData[k]);
        return (
          <Row key={k} field={field} htmlFor={id} hint={field.hint}>
            <span className="inline-flex items-center gap-4">
              <input id={id} type="range" min={field.min} max={field.max} value={formData[k]} aria-valuetext={said}
                onChange={(e) => set(k, parseInt(e.target.value))} className="w-56 accent-cyan-500" />
              <output htmlFor={id} className="w-28 text-[15px] font-medium text-white tabular-nums">{said}</output>
            </span>
          </Row>
        );
      }
      case 'custom':
        return <div key={field.id}>{renderCustom(field)}</div>;
    }
  };

  const renderCustom = (field: Extract<PlanField, { kind: 'custom' }>) => {
    switch (field.id) {
      case 'features':
        return (
          <Row field={field} hint={field.hint} wide>
            <FeatureListEditor value={formData.features} onChange={(v) => set('features', v)} allFeatures={allFeatures} />
          </Row>
        );
      case 'token-settings':
        return (
          <div className="py-4">
            <PackageTokenSettings
              value={{
                token_packs: formData.token_packs,
                out_of_tokens_policy: formData.out_of_tokens_policy,
                out_of_tokens_amount: formData.out_of_tokens_amount,
              }}
              onChange={(t) => setFormData((f) => ({ ...f, ...t }))}
              packsInfo={<InfoTip label="Top-up packs">{field.info}</InfoTip>}
              runOutInfo={field.secondInfo && <InfoTip label={field.secondInfo.label}>{field.secondInfo.info}</InfoTip>}
            />
          </div>
        );
      case 'basemaps-online':
        return (
          <Row field={field} hint={field.hint} wide>
            <BasemapPicker label={field.label} tone="cyan" sources={BASEMAP_OPTIONS.map((b) => b.value)}
              value={formData.allowed_basemaps} onChange={(v) => set('allowed_basemaps', v)} />
          </Row>
        );
      case 'basemaps-offline':
        return (
          <Row field={field} hint={field.hint} wide>
            <BasemapPicker label={field.label} tone="green" sources={OFFLINE_CAPABLE_SOURCES}
              value={formData.allowed_basemaps_offline} onChange={(v) => set('allowed_basemaps_offline', v)} />
            <p className="text-sm text-amber-400 font-medium">
              ⚠️ Google, Bing, Mapbox and HERE prohibit tile caching in their Terms of Service. Enable for testing only, not for production use.
            </p>
            {offlineSourcesInvalid && <p className="text-sm text-red-400">{OFFLINE_INVALID}</p>}
          </Row>
        );
      case 'scout-diagnostics':
        return (
          <Row field={field}
            hint={
              <div className="space-y-2">
                <p>Extra detail in the Scout's pop-outs:</p>
                <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-slate-300">
                  <dt className="text-slate-400">Wi-Fi</dt><dd>IP, gateway, DNS and BSSID</dd>
                  <dt className="text-slate-400">Mobile data</dt><dd>Both IMEIs and the phone number</dd>
                  <dt className="text-slate-400">GPS</dt><dd>System, Record and Tuning tabs, and Reset GPS</dd>
                  <dt className="text-slate-400">Battery</dt><dd>Cell voltage, charge or discharge rate, the last hour, and the time estimate</dd>
                  <dt className="text-slate-400">Device status</dt><dd>What's included, and the account's role</dd>
                </dl>
                <p><span className="font-medium text-slate-200">Also needs admin mode on the Scout</span> (five taps on the version in Settings). With it off, the Scout shows what a customer sees.</p>
              </div>
            }>
            <Switch checked={formData.scout_diagnostics} onChange={(on) => set('scout_diagnostics', on)} label={field.label} />
          </Row>
        );
    }
  };

  // One line under each section in the menu; switches tagged amber (no effect yet) are not counted.
  const summary = (s: PlanSection) => {
    switch (s.id) {
      case 'store': return { active: 'On the pricing page', hidden: 'Hidden from the pricing page', archived: 'Archived' }[formData.visibility];
      case 'price': return formData.manual_expiry ? 'Manual billing' : `${nzd(formData.monthly_price_cents)} a month`;
      case 'tokens': return `${formData.included_tokens.toLocaleString('en-NZ')} a month`;
      case 'weather': return `${formData.weather_days} days ahead`;
    }
    const live = fieldsOf(s).filter((f) => f.keys.length === 1 && typeof formData[f.keys[0]] === 'boolean' && f.badgeTone !== 'amber');
    return `${live.filter((f) => formData[f.keys[0]]).length} of ${live.length} on`;
  };

  const tabClass = (selected: boolean) =>
    `block rounded-lg px-3 py-2 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 ${
      selected ? 'bg-slate-700/70 ring-1 ring-slate-600' : 'hover:bg-slate-700/50'
    }`;

  return (
    // On a wide screen the editor is exactly the screen: the Save bar stays
    // at the top and only the section below it scrolls. (The admin shell lets the
    // window scroll, so position: sticky cannot keep a bar in view.)
    <div className="space-y-6 xl:h-[calc(100vh-7rem)] xl:flex xl:flex-col xl:space-y-4">
      <header className="space-y-3 xl:flex-none">
        <button type="button" onClick={cancel}
          className="inline-flex items-center gap-2 rounded text-[15px] font-medium text-cyan-400 hover:text-cyan-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500">
          <span aria-hidden="true">←</span> All packages
        </button>
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-700 bg-slate-800/70 px-4 py-3">
          {title && <h1 className="mr-2 text-2xl font-bold text-white">{title}</h1>}
          <p className="basis-full sm:basis-auto sm:flex-1 text-[15px] text-slate-300">
            {offlineSourcesInvalid ? (
              <span className="text-red-400">{OFFLINE_INVALID}</span>
            ) : reviewing ? (
              <span className="text-white">Check the changes below, then save.</span>
            ) : dirty ? (
              <span className="text-white">
                {`${changeCount} unsaved change${changeCount > 1 ? 's' : ''} in ${changedSections.map((s) => s.title).join(', ')}`}
              </span>
            ) : (
              'No unsaved changes'
            )}
          </p>
          <button type="button" onClick={cancel} className="ml-auto sm:ml-0 px-4 py-2 text-slate-300 hover:text-white transition-colors">
            Cancel
          </button>
          {!reviewing && <button
            type="button"
            onClick={() => (isNew ? onSave(formData, undefined) : setReviewing(true))}
            disabled={isLoading || offlineSourcesInvalid || (!isNew && !dirty)}
            className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg hover:from-cyan-400 hover:to-blue-500 disabled:opacity-50"
          >
            {isLoading ? 'Saving...' : 'Save Package'}
          </button>}
        </div>

        {reviewing && (
          <section ref={reviewRef} aria-labelledby="plan-review-title"
            className="rounded-xl border border-cyan-500/50 bg-slate-800 p-6 space-y-4 xl:max-h-[55vh] xl:overflow-y-auto">
            <h2 id="plan-review-title" className="text-lg font-semibold text-white">Review changes</h2>
            <p className="rounded-lg bg-amber-500/10 px-4 py-3 text-[15px] text-amber-200">
              {subscriberCount == null
                ? 'Every boat on this plan gets these changes at its next sync.'
                : `This plan has ${subscriberCount} active subscription${subscriberCount === 1 ? '' : 's'}. They get these changes at their next sync.`}
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-[15px]">
                <thead>
                  <tr className="text-left text-sm text-slate-400">
                    <th className="py-2 pr-4 font-medium">Setting</th>
                    <th className="py-2 pr-4 font-medium">Before</th>
                    <th className="py-2 font-medium">After</th>
                  </tr>
                </thead>
                <tbody>
                  {changes.map((c) => (
                    <tr key={c.key} className="border-t border-slate-700 align-top">
                      <td className="py-2 pr-4 text-white">{c.label}</td>
                      <td className="py-2 pr-4 text-slate-400 line-through">{c.from}</td>
                      <td className="py-2 text-cyan-300">{c.to}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div>
              <label htmlFor="plan-change-reason" className="block text-[15px] font-medium text-slate-100">Reason (optional)</label>
              <p className="text-sm text-slate-400">Kept in the plan's history, so you and others can see why it changed.</p>
              <textarea id="plan-change-reason" rows={2} value={reason} maxLength={500}
                onChange={(e) => setReason(e.target.value)} className={`${input} mt-2 w-full`}
                placeholder="e.g. Coastal gets 7 days of forecast from November" />
            </div>
            <div className="flex flex-wrap gap-3">
              <button type="button" disabled={isLoading || offlineSourcesInvalid}
                onClick={() => onSave(formData, reason.trim() || undefined)}
                className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg hover:from-cyan-400 hover:to-blue-500 disabled:opacity-50">
                {isLoading ? 'Saving...' : `Save to ${formData.name || 'this plan'}`}
              </button>
              <button type="button" onClick={() => setReviewing(false)} className="px-4 py-2 text-slate-300 hover:text-white">
                Keep editing
              </button>
            </div>
          </section>
        )}
      </header>

    <div className="lg:grid xl:min-h-0 xl:flex-1 lg:grid-cols-[14rem_minmax(0,1fr)] xl:grid-cols-[14rem_minmax(0,1fr)_21rem] lg:gap-6">
      <div role="tablist" aria-label="Plan sections" aria-orientation="vertical"
        className="mb-4 flex flex-wrap gap-2 lg:mb-0 lg:col-start-1 lg:row-start-1 lg:row-span-2 lg:flex-col lg:flex-nowrap lg:gap-1 xl:min-h-0 xl:overflow-y-auto">
        {PLAN_SECTIONS.map((s) => {
          const changed = changedSections.includes(s);
          const attention = checks.filter((c) => c.section === s.id);
          const problem = attention.some((c) => c.level === 'problem');
          const selected = tab === s.id;
          return (
            <button key={s.id} type="button" role="tab" id={`plan-tab-${s.id}`} aria-controls={`plan-${s.id}`}
              aria-selected={selected} tabIndex={selected ? 0 : -1}
              onClick={() => openTab(s.id)} onKeyDown={onTabKey} className={`w-auto lg:w-full ${tabClass(selected)}`}>
              <span className="flex items-center justify-between gap-2">
                <span className={`text-[15px] font-medium ${s.danger ? 'text-red-300' : selected ? 'text-white' : 'text-slate-100'}`}>{s.title}</span>
                <span className="flex items-center gap-1.5" aria-hidden="true">
                  {attention.length > 0 && <span className={`h-2 w-2 rounded-full ${problem ? 'bg-red-400' : 'bg-amber-400'}`} />}
                  {changed && <span className="h-2 w-2 rounded-full bg-cyan-400" />}
                </span>
              </span>
              <span className="hidden lg:block text-[13px] text-slate-400">{summary(s)}</span>
              {changed && <span className="sr-only"> (changed)</span>}
              {attention.length > 0 && <span className="sr-only"> (needs attention)</span>}
            </button>
          );
        })}
        {history && (
          <button type="button" role="tab" id="plan-tab-history" aria-controls="plan-history"
            aria-selected={tab === 'history'} tabIndex={tab === 'history' ? 0 : -1}
            onClick={() => openTab('history')} onKeyDown={onTabKey}
            className={`w-auto lg:w-full lg:mt-3 lg:border-t lg:border-slate-700 lg:pt-3 ${tabClass(tab === 'history')}`}>
            <span className="text-[15px] font-medium text-slate-100">History</span>
            <span className="hidden lg:block text-[13px] text-slate-400">
              {history.length === 0 ? 'No changes yet' : `${history.length} saved change${history.length === 1 ? '' : 's'}`}
            </span>
          </button>
        )}
      </div>

      <aside className="mb-6 lg:mb-0 lg:col-start-2 lg:row-start-1 xl:col-start-3 xl:row-span-2 xl:min-h-0 xl:overflow-y-auto">
        <PlanSidebar form={formData} checks={checks} />
      </aside>

      {/* Every section stays mounted, so what was typed on one tab is still there on return. */}
      <div ref={panelsRef} className="min-w-0 xl:min-h-0 xl:overflow-y-auto lg:col-start-2 lg:row-start-2 xl:row-start-1 xl:row-span-2">
        {PLAN_SECTIONS.map((s) => (
          <section key={s.id} id={`plan-${s.id}`} role="tabpanel" aria-labelledby={`plan-${s.id}-title`} hidden={tab !== s.id}
            className={`rounded-xl border p-6 ${s.danger ? 'border-red-500/40 bg-red-950/10' : 'border-slate-700 bg-slate-800/50'}`}>
            <h2 id={`plan-${s.id}-title`} className={`text-lg font-semibold ${s.danger ? 'text-red-300' : 'text-white'}`}>{s.title}</h2>
            <p className="mt-1 text-sm text-slate-400">{s.question}</p>
            {s.groups.map((g, i) => (
              <div key={i} className="mt-4">
                {g.title && <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-400">{g.title}</h3>}
                <div>{g.fields.map(renderField)}</div>
              </div>
            ))}
          </section>
        ))}

        {history && <PlanHistory history={history} hidden={tab !== 'history'} />}
      </div>
    </div>
    </div>
  );
}
